/**
 * Custom validation errors for 526EZ form
 * This function is called during form submission to add form-specific validation
 * errors that aren't captured by schema validation.
 *
 * @param {Object} formData - The current form data
 * @returns {Array} Array of error objects matching jsonschema validator format
 */
import { getPrefillZipResult } from '../../shared/utils/zipCodeData';

export const getCustomValidationErrors = formData => {
  if (!formData) return [];

  const customErrors = [];

  // Prefill zip bypass: a prefilled mailing-address zip is never verified by
  // the ZipCodeInput component (which only fires on keystroke), so an invalid
  // one slips through. onFormLoaded verifies it into a prefill-only store; here
  // — at submit — we surface it. Only block if the stored result is for the zip
  // still in the form (if the user edited it, the live component validation
  // owns the new value, so we defer to that).
  const prefillZip = getPrefillZipResult();
  const currentZip = formData?.mailingAddress?.zipCode;
  if (
    prefillZip &&
    prefillZip.isValid === false &&
    currentZip &&
    String(currentZip).slice(0, 5) === prefillZip.zip
  ) {
    customErrors.push({
      property: 'instance.mailingAddress',
      message: 'requires property "zipCode"',
      name: 'required',
      argument: 'zipCode',
      stack: 'instance.mailingAddress requires property "zipCode"',
    });
  }

  const claimType = formData?.['view:claimType'];
  const claimingNew = claimType?.['view:claimingNew'];
  const claimingIncrease = claimType?.['view:claimingIncrease'];

  // Check if user is claiming new conditions but hasn't added any new disabilities.
  if (claimingNew) {
    const newDisabilities = formData?.newDisabilities;
    if (
      !newDisabilities ||
      !Array.isArray(newDisabilities) ||
      newDisabilities.length === 0
    ) {
      customErrors.push({
        property: 'instance.newDisabilities',
        message: 'does not meet minimum length of 1',
        name: 'minItems',
        argument: 1,
        stack: 'instance.newDisabilities does not meet minimum length of 1',
      });
    }
  }

  // Check if no claim type has been selected (both new and increase are explicitly false).
  // Add this error when:
  // - there are no usable condition rows at all (missing/empty/only empty objects), or
  // - at least one condition has ratedDisability set but the claimType flags are still both false.
  // When there ARE non-empty conditions but none has ratedDisability, don't show this error
  const items = formData?.newDisabilities;
  const hasItemsArray = Array.isArray(items);
  const hasAnyRow = hasItemsArray && items.length > 0;
  const hasNonEmptyCondition =
    hasAnyRow && items.some(item => item && Object.keys(item).length > 0);
  const someConditionHasRatedDisability =
    hasNonEmptyCondition && items.some(item => item?.ratedDisability);

  const shouldShowClaimTypeError =
    claimType &&
    claimingNew === false &&
    claimingIncrease === false &&
    (!hasItemsArray ||
      !hasAnyRow ||
      !hasNonEmptyCondition ||
      someConditionHasRatedDisability);

  if (shouldShowClaimTypeError) {
    customErrors.push({
      property: 'instance.view:claimType',
      message: 'Please select at least one type of condition',
      name: 'required',
      argument: undefined,
      stack: 'instance.view:claimType is required',
    });
  }

  return customErrors;
};
